import 'twin.macro';
import React, { RefObject } from 'react';
import { AttachmentComponent as Attachments } from '@teetsh/app/src/components/Attachments/Attachment';
import { Field, useField } from 'react-final-form';
import { useUser } from '@teetsh/app/src/containers/App/context/useUser';
import MaterialItems from '@teetsh/app/src/components/Materials/Items';
import { LinkedFichesDePrep } from 'containers/CahierJournal/EventModal/ModalBody/Classic/LinkedFichesDePreps';
import { useTranslation } from 'react-i18next';
import { LinkedProgItem } from 'containers/CahierJournal/EventModal/ModalBody/Classic/LinkedProgItem';
import { Referentiel } from './Referentiel';
import { Flow } from './Flow';
import { CustomFieldsComponent } from 'components/NewCustomFields/CustomFieldsComponent';
import { useEventCustomFieldDefinitionsContext } from 'containers/App/context/EventCustomFieldDefinitionsContext/EventCustomFieldDefinitionsProvider';
import { BriefcaseIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import useNotification from 'containers/App/Notifications/useNotification';

interface Props {
  materialInputRef?: RefObject<HTMLInputElement>;
  attachmentInputRef?: RefObject<HTMLInputElement>;
  customFieldsInputRefs?: RefObject<HTMLInputElement[]>;
  cycle?: string;
  onAddToSeanceCallback?: () => void;
}

export function ClassicBody(props: Props) {
  const {
    materialInputRef,
    attachmentInputRef,
    customFieldsInputRefs,
    cycle,
    onAddToSeanceCallback,
  } = props;
  const user = useUser();
  const ateliersField = useField('ateliers');
  const hasAteliers = ateliersField.input.value.length > 0;
  const boId = user.referencielId;
  const titleField = useField('title');
  const { t } = useTranslation(['cahierJournal', 'common']);
  const {
    definitions: eventCustomFieldDefinitions,
    deleteDefinition,
    updateDefinition,
  } = useEventCustomFieldDefinitionsContext();
  const { notifySuccess } = useNotification();

  return (
    <>
      <Field
        name="bo"
        render={({ input: { value, onChange } }) => (
          <Referentiel
            hasAteliers={hasAteliers}
            value={value}
            onChange={onChange}
            titleValue={titleField.input.value}
            cycle={cycle}
            boId={boId}
          />
        )}
      />
      <Field
        name="flow"
        placeholder={t('cahierJournal:seance.modal.flowPlaceholder')}
        render={({ input: { value, onChange } }) => (
          <Flow hasAteliers={hasAteliers} value={value} onChange={onChange} />
        )}
      />
      <Field
        name="attachments"
        render={({ input: { onChange, value } }) => {
          if ((value || []).length === 0) {
            return null;
          }
          return (
            <div>
              <h4 tw="text-neutral-moderate mb-2 flex items-center gap-1">
                <PaperClipIcon tw="inline-block h-5 w-5" />
                {t('cahierJournal:seance.modal.attachments')}
              </h4>
              <Attachments
                inputRef={attachmentInputRef}
                attachments={value}
                onChange={onChange}
              />
            </div>
          );
        }}
      />
      <Field
        name="linkedFichesDePrep"
        render={({ input: { onChange, value } }) => (
          // Do not early return null here, because there is some useEffect wizardry going on in LinkedFichesDePrep for the Drawer
          <LinkedFichesDePrep
            allowAddAnother={!user.guestMode?.enabled}
            linkedFichesDePrep={value}
            onChange={v => {
              onAddToSeanceCallback?.();
              onChange(v);
            }}
          />
        )}
      />
      <Field
        name="programmationItemId"
        render={({ input: { onChange, value } }) => (
          // Do not early return null here, because there is some useEffect wizardry going on in LinkedProgItem for the Drawer
          <LinkedProgItem
            programmationItemId={value}
            // c'est important ici le `v => onChange(v)` et pas seulement `onChange`
            // ça permet de bien mettre à jour le useEffect qu'on trouve dans
            // LinkedProgItem
            // donc ne pas mettre seulement onChange ici
            // sinon si tu lis 2 fois un élément de prog tu vas avoir des
            // des problèmes, cf TSH-842
            onChange={v => {
              onAddToSeanceCallback?.();
              onChange(v);
            }}
          />
        )}
      />
      <Field
        name="materials"
        render={({ input: { onChange, value } }) => {
          if ((value || []).length === 0) {
            return null;
          }
          return (
            <div>
              <h4 tw="text-neutral-moderate mb-2 flex items-center gap-1">
                <BriefcaseIcon tw="inline-block h-5 w-5" />
                {t('cahierJournal:seance.materials')}
              </h4>
              <MaterialItems
                inputRef={materialInputRef}
                onChange={v => {
                  notifySuccess({
                    title: t('common:components.materials.materialSavedToList'),
                  });
                  return onChange(v);
                }}
                items={value}
                enableEdit={!user.guestMode?.enabled}
              />
            </div>
          );
        }}
      />
      <Field
        name="customFields"
        render={({ input: { onChange, value } }) => {
          const matchingDefinitions = eventCustomFieldDefinitions.filter(def =>
            (value || []).some(v => v.id === def.id),
          );
          if (matchingDefinitions.length === 0) {
            return null;
          }
          return (
            <div>
              <h4 tw="text-neutral-moderate mb-2 flex items-center gap-1">
                {t('cahierJournal:seance.modal.customElements')}
              </h4>
              <CustomFieldsComponent
                customFieldsInputRefs={customFieldsInputRefs}
                items={value}
                onChange={onChange}
                customFieldDefinitions={eventCustomFieldDefinitions.filter(
                  def => def.targets.includes('cahierJournal'),
                )}
                onUpdateDefinition={updateDefinition}
                onDeleteDefinition={def => deleteDefinition(def.id)}
              />
            </div>
          );
        }}
      />
    </>
  );
}
